/**
 * Notification Service
 * API calls for in-app notification system
 */

import API_BASE_URL from '../config';

const API_BASE = API_BASE_URL;

// ============================================================================
// TYPES
// ============================================================================

export interface NotificationFromUser {
  id: number;
  name: string | null;
  email: string | null;
}

export interface Notification {
  id: number;
  type: string;
  title: string;
  message: string | null;
  group_id: number | null;
  group_name: string | null;
  collection_id: number | null;
  collection_name: string | null;
  item_id: number | null;
  from_user: NotificationFromUser | null;
  action_url: string | null;
  action_data: Record<string, any> | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  requires_action: boolean;
  action_status: 'pending' | 'accepted' | 'declined' | null;
}

export interface NotificationsResponse {
  notifications: Notification[];
  unread_count: number;
  total: number;
}

export interface UnreadCountResponse {
  unread_count: number;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Get all notifications for the current user
 */
export const getNotifications = async (
  token: string,
  options: { unreadOnly?: boolean; limit?: number; offset?: number } = {}
): Promise<NotificationsResponse> => {
  const params = new URLSearchParams();
  if (options.unreadOnly) params.append('unread_only', 'true');
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.offset) params.append('offset', options.offset.toString());

  const queryString = params.toString();
  const url = `${API_BASE}/api/notifications${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch notifications');
  }

  return response.json();
};

/**
 * Get the count of unread notifications
 */
export const getUnreadCount = async (token: string): Promise<number> => {
  const response = await fetch(`${API_BASE}/api/notifications/unread-count`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch unread count');
  }

  const data: UnreadCountResponse = await response.json();
  return data.unread_count;
};

/**
 * Mark a notification as read
 */
export const markAsRead = async (token: string, notificationId: number): Promise<void> => {
  const response = await fetch(`${API_BASE}/api/notifications/${notificationId}/read`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to mark notification as read');
  }
};

/**
 * Mark all notifications as read
 */
export const markAllAsRead = async (token: string): Promise<number> => {
  const response = await fetch(`${API_BASE}/api/notifications/mark-all-read`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to mark all notifications as read');
  }

  const data = await response.json();
  return data.updated_count;
};

/**
 * Delete a notification
 */
export const deleteNotification = async (token: string, notificationId: number): Promise<void> => {
  const response = await fetch(`${API_BASE}/api/notifications/${notificationId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete notification');
  }
};

/**
 * Clear all notifications
 */
export const clearAllNotifications = async (token: string): Promise<number> => {
  const response = await fetch(`${API_BASE}/api/notifications/clear-all`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to clear notifications');
  }

  const data = await response.json();
  return data.deleted_count;
};

/**
 * Handle a notification action (accept/decline invite, etc.)
 */
export const handleNotificationAction = async (
  token: string,
  notificationId: number,
  action: 'accept' | 'decline'
): Promise<{ success: boolean; action_status: string }> => {
  const response = await fetch(`${API_BASE}/api/notifications/${notificationId}/action`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ action })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to handle notification action');
  }

  return response.json();
};

// ============================================================================
// NOTIFICATION TYPE HELPERS
// ============================================================================

export const getNotificationIcon = (type: string): string => {
  switch (type) {
    case 'group_invite':
      return '👥';
    case 'collection_shared':
      return '📁';
    case 'member_joined':
      return '👋';
    case 'member_left':
      return '👤';
    case 'item_added':
      return '➕';
    case 'item_updated':
      return '✏️';
    case 'permission_changed':
      return '🔐';
    case 'guest_activity':
      return '🧑‍🤝‍🧑';
    default:
      return '🔔';
  }
};

export const getNotificationColor = (type: string): string => {
  switch (type) {
    case 'group_invite':
      return 'var(--color-accent)'; // Blue
    case 'collection_shared':
      return '#34C759'; // Green
    case 'member_joined':
      return '#5856D6'; // Purple
    case 'member_left':
      return '#FF9500'; // Orange
    case 'item_added':
      return '#34C759'; // Green
    case 'item_updated':
      return 'var(--color-accent)'; // Blue
    case 'permission_changed':
      return '#FF9500'; // Orange
    case 'guest_activity':
      return '#5856D6'; // Purple
    default:
      return '#8E8E93'; // Gray
  }
};

export const formatNotificationTime = (createdAt: string): string => {
  const now = new Date();
  const created = new Date(createdAt);
  const diffMs = now.getTime() - created.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return 'Just now';
  } else if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return created.toLocaleDateString();
  }
};
